import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/analytics"

export default class extends Controller {
  static targets = [
    "step", "stepIndicator", "stepLabel", "stepLine",
    "firstName", "lastName", "email", "phone",
    "domain", "jurisdiction", "jurisdictionOther", "legalAid", "description", "adverseParty",
    "slot", "penalWarning", "jurisdictionError",
    "error", "errorText",
    "progress", "navigation", "backButton", "nextButton", "nextButtonText", "loadingSpinner",
    "selectedSlotDisplay", "selectedSlotText",
    "recapClient", "recapSlot",
    "stripeElements", "payButton", "payButtonText", "payButtonIcon", "payButtonSpinner",
    "successEmail", "successSlot"
  ]

  static values = {
    stripeKey: String
  }

  connect() {
    this.currentStep = "info"
    this.steps = ["info", "details", "calendar", "payment", "success"]
    this.clientSecret = null
    this.stripe = null
    this.elements = null

    this.updateStepDisplay()
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Domain change handler
    if (this.hasDomainTarget) {
      this.domainTarget.addEventListener("change", () => this.onDomainChange())
    }

    // Jurisdiction change handler
    if (this.hasJurisdictionTarget) {
      this.jurisdictionTarget.addEventListener("change", () => this.onJurisdictionChange())
    }

    // Legal aid change handler
    this.legalAidTargets.forEach(radio => {
      radio.addEventListener("change", () => this.checkJurisdictionRestriction())
    })
  }

  onDomainChange() {
    const value = this.domainTarget.value
    if (this.hasPenalWarningTarget) {
      if (value === "penal") {
        this.penalWarningTarget.classList.remove("hidden")
      } else {
        this.penalWarningTarget.classList.add("hidden")
      }
    }
  }

  onJurisdictionChange() {
    const value = this.jurisdictionTarget.value
    if (this.hasJurisdictionOtherTarget) {
      if (value === "autre") {
        this.jurisdictionOtherTarget.classList.remove("hidden")
      } else {
        this.jurisdictionOtherTarget.classList.add("hidden")
      }
    }
    this.checkJurisdictionRestriction()
  }

  checkJurisdictionRestriction() {
    const jurisdiction = this.jurisdictionTarget?.value
    const legalAid = this.legalAidTargets.find(r => r.checked)?.value

    if (this.hasJurisdictionErrorTarget) {
      if (jurisdiction === "saint-denis" && legalAid === "oui") {
        this.jurisdictionErrorTarget.classList.remove("hidden")
        this.jurisdictionErrorTarget.classList.add("flex")
      } else {
        this.jurisdictionErrorTarget.classList.add("hidden")
        this.jurisdictionErrorTarget.classList.remove("flex")
      }
    }
  }

  updateStepDisplay() {
    const currentIndex = this.steps.indexOf(this.currentStep)

    // Update step visibility
    this.stepTargets.forEach(step => {
      const stepName = step.dataset.step
      if (stepName === this.currentStep) {
        step.classList.remove("hidden")
      } else {
        step.classList.add("hidden")
      }
    })

    // Update progress indicators
    this.stepIndicatorTargets.forEach((indicator, index) => {
      indicator.classList.remove("bg-vert", "bg-terracotta", "bg-beige", "text-white", "text-charbon/50")

      if (index < currentIndex) {
        indicator.classList.add("bg-vert", "text-white")
        indicator.innerHTML = "✓"
      } else if (index === currentIndex) {
        indicator.classList.add("bg-terracotta", "text-white")
        indicator.innerHTML = index + 1
      } else {
        indicator.classList.add("bg-beige", "text-charbon/50")
        indicator.innerHTML = index + 1
      }
    })

    // Update step lines
    this.stepLineTargets.forEach((line, index) => {
      line.classList.remove("bg-vert", "bg-beige")
      if (index < currentIndex) {
        line.classList.add("bg-vert")
      } else {
        line.classList.add("bg-beige")
      }
    })

    // Update step labels
    this.stepLabelTargets.forEach((label, index) => {
      label.classList.remove("font-medium", "text-charbon", "text-charbon/50")
      if (index === currentIndex) {
        label.classList.add("font-medium", "text-charbon")
      } else {
        label.classList.add("text-charbon/50")
      }
    })

    // Update navigation visibility
    if (this.hasNavigationTarget) {
      if (this.currentStep === "success" || this.currentStep === "payment") {
        this.navigationTarget.classList.add("hidden")
      } else {
        this.navigationTarget.classList.remove("hidden")
      }
    }

    // Update progress visibility
    if (this.hasProgressTarget) {
      if (this.currentStep === "success") {
        this.progressTarget.classList.add("hidden")
      } else {
        this.progressTarget.classList.remove("hidden")
      }
    }
  }

  validateStep() {
    this.hideError()

    switch (this.currentStep) {
      case "info":
        if (!this.firstNameTarget.value.trim() || !this.lastNameTarget.value.trim() || !this.emailTarget.value.trim()) {
          this.showError("Veuillez remplir tous les champs obligatoires")
          return false
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.emailTarget.value)) {
          this.showError("Veuillez entrer une adresse email valide")
          return false
        }
        return true

      case "details":
        if (!this.domainTarget.value || !this.jurisdictionTarget.value) {
          this.showError("Veuillez remplir tous les champs obligatoires")
          return false
        }
        const legalAid = this.legalAidTargets.find(r => r.checked)?.value
        if (!legalAid) {
          this.showError("Veuillez indiquer si vous beneficiez de l'aide juridictionnelle")
          return false
        }
        if (this.jurisdictionTarget.value === "autre" && !this.jurisdictionOtherTarget.value.trim()) {
          this.showError("Veuillez preciser la juridiction")
          return false
        }
        // Check jurisdiction restriction
        if (this.jurisdictionTarget.value === "saint-denis" && legalAid === "oui") {
          return false
        }
        return true

      case "calendar":
        if (!this.slotTarget.value) {
          this.showError("Veuillez selectionner un creneau")
          return false
        }
        return true

      default:
        return true
    }
  }

  async nextStep() {
    if (!this.validateStep()) return

    track("rdv-etape", { etape: this.currentStep })

    const currentIndex = this.steps.indexOf(this.currentStep)

    if (this.currentStep === "calendar") {
      // Create payment intent before going to payment step
      await this.createPaymentIntent()
    } else if (currentIndex < this.steps.length - 1) {
      this.currentStep = this.steps[currentIndex + 1]
      this.updateStepDisplay()
    }
  }

  prevStep() {
    const currentIndex = this.steps.indexOf(this.currentStep)
    if (currentIndex > 0) {
      this.currentStep = this.steps[currentIndex - 1]
      this.hideError()
      this.updateStepDisplay()
    } else {
      // Go back to contact choice
      const contactFormController = this.element.closest("[data-controller~='contact-form']")
      if (contactFormController) {
        contactFormController.querySelector("[data-contact-form-target='choice']").classList.remove("hidden")
        contactFormController.querySelector("[data-contact-form-target='booking']").classList.add("hidden")
      }
    }
  }

  async createPaymentIntent() {
    this.setLoading(true)

    const formData = {
      firstName: this.firstNameTarget.value,
      lastName: this.lastNameTarget.value,
      email: this.emailTarget.value,
      phone: this.phoneTarget?.value || "",
      domain: this.domainTarget.value,
      jurisdiction: this.jurisdictionTarget.value === "autre" ? this.jurisdictionOtherTarget.value : this.jurisdictionTarget.value,
      jurisdictionOther: this.jurisdictionOtherTarget?.value || "",
      legalAid: this.legalAidTargets.find(r => r.checked)?.value,
      description: this.descriptionTarget?.value || "",
      adverseParty: this.adversePartyTarget?.value || "",
      slot: this.slotTarget.value,
      paymentType: "deposit"
    }

    try {
      const response = await fetch("/api/bookings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.code === "JURISDICTION_RESTRICTION") {
          this.currentStep = "details"
          this.updateStepDisplay()
          this.checkJurisdictionRestriction()
        } else {
          this.showError(data.error || "Erreur lors de la creation du paiement")
        }
        this.setLoading(false)
        return
      }

      this.clientSecret = data.clientSecret

      track("rdv-paiement-affiche", { domaine: formData.domain })

      // Update recap
      if (this.hasRecapClientTarget) {
        this.recapClientTarget.textContent = `${formData.firstName} ${formData.lastName}`
      }
      if (this.hasRecapSlotTarget) {
        this.recapSlotTarget.textContent = this.formatSlot(formData.slot)
      }

      this.currentStep = "payment"
      this.updateStepDisplay()

      // Initialize Stripe
      await this.initializeStripe()

    } catch (error) {
      console.error("Error creating payment:", error)
      this.showError("Erreur de connexion au serveur")
    } finally {
      this.setLoading(false)
    }
  }

  async initializeStripe() {
    // Load Stripe.js if not already loaded
    if (!window.Stripe) {
      const stripeKey = document.querySelector("meta[name='stripe-key']")?.content
      if (!stripeKey) {
        this.showError("Configuration Stripe manquante")
        return
      }
      this.stripe = Stripe(stripeKey)
    } else {
      const stripeKey = document.querySelector("meta[name='stripe-key']")?.content
      this.stripe = Stripe(stripeKey)
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#C2674E",
          colorBackground: "#ffffff",
          colorText: "#3A3A3A",
          fontFamily: "Inter, sans-serif",
          borderRadius: "12px"
        }
      },
      locale: "fr"
    })

    const paymentElement = this.elements.create("payment")
    paymentElement.mount(this.stripeElementsTarget)
  }

  async confirmPayment() {
    if (!this.stripe || !this.elements) {
      this.showError("Veuillez patienter le temps que le formulaire de paiement charge")
      return
    }

    this.setPayButtonLoading(true)

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: window.location.origin + "/rdv-confirme"
      },
      redirect: "if_required"
    })

    if (error) {
      track("rdv-paiement-erreur")
      this.showError(error.message || "Erreur lors du paiement")
      this.setPayButtonLoading(false)
    } else {
      // Payment succeeded
      track("rdv-paye", { domaine: this.domainTarget.value })
      this.currentStep = "success"
      this.updateStepDisplay()

      if (this.hasSuccessEmailTarget) {
        this.successEmailTarget.textContent = this.emailTarget.value
      }
      if (this.hasSuccessSlotTarget) {
        this.successSlotTarget.textContent = this.formatSlot(this.slotTarget.value)
      }
    }
  }

  setPayButtonLoading(loading) {
    if (this.hasPayButtonTarget) {
      this.payButtonTarget.disabled = loading
    }
    if (this.hasPayButtonIconTarget && this.hasPayButtonSpinnerTarget) {
      if (loading) {
        this.payButtonIconTarget.classList.add("hidden")
        this.payButtonSpinnerTarget.classList.remove("hidden")
      } else {
        this.payButtonIconTarget.classList.remove("hidden")
        this.payButtonSpinnerTarget.classList.add("hidden")
      }
    }
    if (this.hasPayButtonTextTarget) {
      this.payButtonTextTarget.textContent = loading ? "Paiement en cours..." : "Confirmer et payer"
    }
  }

  formatSlot(slot) {
    if (!slot) return ""
    const date = new Date(slot)
    return new Intl.DateTimeFormat("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date)
  }

  showError(message) {
    if (this.hasErrorTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
      this.errorTarget.classList.add("flex")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
      this.errorTarget.classList.remove("flex")
    }
  }

  setLoading(loading) {
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = loading
    }
    if (this.hasLoadingSpinnerTarget) {
      if (loading) {
        this.loadingSpinnerTarget.classList.remove("hidden")
        if (this.hasNextButtonTextTarget) {
          this.nextButtonTextTarget.textContent = "Chargement..."
        }
      } else {
        this.loadingSpinnerTarget.classList.add("hidden")
        if (this.hasNextButtonTextTarget) {
          this.nextButtonTextTarget.textContent = "Continuer"
        }
      }
    }
  }

  // Called from calendar controller when a slot is selected
  slotSelected(slot) {
    if (this.hasSlotTarget) {
      this.slotTarget.value = slot
    }
    if (this.hasSelectedSlotDisplayTarget) {
      this.selectedSlotDisplayTarget.classList.remove("hidden")
    }
    if (this.hasSelectedSlotTextTarget) {
      this.selectedSlotTextTarget.textContent = this.formatSlot(slot)
    }
  }
}
